import * as React from "react";
import { Dispatch, useCallback, useEffect, useMemo, useState } from "react";
import { styled, Theme } from "@mui/material";
import {
  PanelContext,
  PatientCase,
  PatientCaseGrouping,
} from "../../../context/diagnosticAgent";
import {
  DataInput,
  DataInputPanel,
  dedupQualifiedFeatures,
  disjointQualifiedFeatures,
} from "../../dataInputTypes";
import {
  Category,
  DiseaseCategoryType,
  QualifiedFeature,
} from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { DataInputPanelEdit } from "../../DataInputPanelEdit";
import { TabSelection } from "./DataInputPanelArea";
import {
  DRUG_OR_MEDICAMENT,
  KahunCategories,
  KahunPanelSubGroup,
  Relations,
} from "shared";
import { DataInputSectionConfiguration } from "../config/configurationTypes";
import {
  DataInputSectionConfigurations,
  isCompatibleDataInputSection,
} from "../config/dataInputSectionConfiguration";
import { SectionMainTitle } from "../common/SectionMainTitle";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { compareByShortName } from "./useOrganizedPanels";
import { PanelsSubGroupMenu } from "./PanelsSubGroupMenu";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { SectionTabs } from "../common/SectionTabs";
import { useDiseasesSections } from "../hooks/useDiseasesSections";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { PanelsSearch } from "./panelsSearch";
import { useRouteHelper } from "../useRouteHelper";
import { PanelsSubCategorySelect } from "./panelsSubCategorySelect";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { DataInputMobileSidePadding } from "../common/styles/consts";
import { useSuggestionsForPlan } from "../hooks/useSuggestionsForPlan";
import { useForceDDOnceInWizard } from "../hooks/useForceDDOnceInWizard";
import { AgentRoute } from "../util/agentRoutes";
import { useScreenSize } from "../util/useScreenSize";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";

//region [[ Styles ]]

export const PanelsListView = styled("div")({});

export const PanelContent = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    paddingRight: `${DataInputMobileSidePadding}px`,
    paddingLeft: `${DataInputMobileSidePadding}px`,
  },
}));

export const PanelTop = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginRight: theme.spacing(1),
  [theme.breakpoints.down(MobileView)]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: KahunColorMap.PrimaryDim,
    marginRight: 0,
    paddingTop: theme.spacing(2),
    paddingRight: `${DataInputMobileSidePadding}px`,
    paddingBottom: theme.spacing(1),
    paddingLeft: `${DataInputMobileSidePadding}px`,
  },
}));

const PanelTitle = styled(SectionMainTitle)(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    color: KahunColorMap.White,
    alignItems: "center",
    "& svg": {
      color: KahunColorMap.White,
    },
  },
}));

const SubCategoryArea = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down(MobileView)]: {
    flexDirection: "row",
    marginBottom: 0,
    width: "100%",
  },
}));

export const CategorySection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  marginTop: "10px",
  justifyContent: "space-between",
}));

type PanelSubTopProps = { theme?: Theme; tabs: "true" | "false" };
export const PanelSubTop = styled("div")(
  ({ theme, tabs }: PanelSubTopProps) => ({
    [theme.breakpoints.down(MobileView)]: {
      marginBottom: "10px",
      padding: 0,
      width: "100%",
      background:
        tabs === "true" ? KahunColorMap.White : KahunColorMap.PrimaryDim,
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PanelsListProps {
  //topLevelItems: TopLevelPanelItem[];
  subGroups: Category[];
  panelsInSubGroup: (
    c: Category,
  ) => { loading: boolean; panels: DataInputPanel[] };
  currentCase: PatientCase;
  selection: TabSelection;
  setSelection?: Dispatch<TabSelection>;
  setResponse: any;
  getCurrentResponse: any;
  loading: boolean;
  showTabs?: boolean;
  onChangeSection?: (section: PatientCaseGrouping) => void;
  singlePanel?: boolean;
  sectionTitle?: string;
  selectedPanelId: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
function configForCategory(cat: Category): DataInputSectionConfiguration {
  return DataInputSectionConfigurations[cat?.id] || {};
}

/**
 * Add findings which were added manualy via the search bar, to their appropriate panel
 * @param list
 * @param findings
 */
function buildPanelListWithResponsesAndSuggestions(
  list: DataInputPanel[],
  findings: QualifiedFeature[],
  getResponse: (qf: QualifiedFeature, s: PatientCaseGrouping) => DataInput,
  section: PatientCaseGrouping,
): DataInputPanel[] {
  const panelFindings = dedupQualifiedFeatures(list.flatMap((l) => l.findings));

  const extraFindings = disjointQualifiedFeatures(
    panelFindings,
    findings,
  ).filter((qf) => !qf.skip);

  let panel: DataInputPanel = null;

  // else add any extra findings to existing panel
  if (extraFindings.length > 0 && list.length === 1) {
    panel = { ...list[0] };
    list = [panel];
    panel.customFindings = extraFindings;
  }

  const labFindings = extraFindings.filter(
    (finding) => finding.feature.category.id !== KahunCategories.PROCEDURE,
  );

  if (section === PatientCaseGrouping.WORKUP && labFindings.length > 0) {
    const newPanel = {
      title: "Other",
      id: KahunPanelSubGroup.Other,
      findings: [],
      customFindings: [],
    };
    list = [...list, newPanel];
    newPanel.findings = extraFindings;
  }

  return list;
}

function qfPanelRelation(qf: QualifiedFeature) {
  return (
    qf.qualifiers &&
    qf.qualifiers.find((q) => q.relation.id === Relations.PANEL_ID)?.value
  );
}

/**
 * custom findings are findings which were manually added via the searchbar
 * This function finds any custom findings which belong in the specified category
 * @param cat
 * @param responses
 */
function customFindingsInSubGroup(
  cat: Category,
  currentCase: QualifiedFeature[],
  section: PatientCaseGrouping,
) {
  const config = configForCategory(cat);
  if (!config?.defaultForFeatures && !config?.defaultForCategories) return [];

  let extra = dedupQualifiedFeatures([...currentCase]).filter(
    (qf) =>
      qfPanelRelation(qf) == null &&
      isCompatibleDataInputSection(qf, section, config),
  );
  return extra;
}

export const PANEL_OTHERS_ID = KahunPanelSubGroup.Other;
export const MOST_LIKELY_ID = KahunPanelSubGroup.MostRelevant;
const CATEGORIES_FOR_MOST_RELEVANT_PE = [DiseaseCategoryType.OutcomeSignsOnly];
const CATEGORIES_FOR_MOST_RELEVANT_ROS = [
  DiseaseCategoryType.OutcomeSymptomOnly,
];
//endregion [[ Functions ]]

// TODO: Move this to useDataInputPanels(), and make the PanelsList get the panels
//       using const { panels, drugFeature, loading } = useDataInputPanels();
//       instead of pulling the props through
//       (VitalSignsSection/DataEntrySection/ClinicalFeaturesSection/BackgroundSection/WorkupSection)
//          -> PatientDataInputView-> DataInputPanelArea-> PanelsList )
let drugFeature;

export const PanelsList = ({ ...props }: PanelsListProps) => {
  const {
    subGroups,
    selection,
    currentCase,
    getCurrentResponse,
    panelsInSubGroup,
    singlePanel,
    sectionTitle,
    selectedPanelId,
  } = props;

  // for each subGroup, selected panelid

  const [isUserSelectedPanel, setIsUserSelectedPanel] = useState(false);

  const { navigateTo } = useRouteHelper();

  const MAX_MOST_LIKELY_SUGGESTIONS = 10;

  const selectedPanelID = selectedPanelId;

  const { inWizard } = useDiagnosticNavigation();

  const { loading: ddLoadingState } = useDiseasesSections();

  const dao = useDaoContext();

  const isSignsAndSymptoms = useMemo(
    () =>
      (selection.section === PatientCaseGrouping.CLINICAL_FEATURES ||
        selection.section === PatientCaseGrouping.REVIEW_OF_SYSTEMS) &&
      selection.subgroup &&
      (selection.subgroup.id === KahunPanelSubGroup.SignSymptom ||
        selection.subgroup.id === KahunPanelSubGroup.Symptoms),
    [selection],
  );

  /*const isSigns = useMemo(
    () =>
      selection.section === PatientCaseGrouping.CLINICAL_FEATURES &&
      selection.subgroup &&
      selection.subgroup.id === KahunPanelSubGroup.SignSymptom,
    [selection],
  );*/

  const isSymptoms = useMemo(
    () =>
      selection.section === PatientCaseGrouping.REVIEW_OF_SYSTEMS &&
      selection.subgroup &&
      selection.subgroup.id === KahunPanelSubGroup.Symptoms,
    [selection],
  );

  // Get the drugQf
  useEffect(() => {
    async function fetchData() {
      const drugQf = await dao.getQualifiedFeatureForId(DRUG_OR_MEDICAMENT);
      drugFeature = drugQf;
    }
    if (drugFeature) return;
    fetchData();
  }, [dao]);

  const {
    findingSuggestions: nextStepSuggestions,
    planIsLoading: planLoading,
  } = useSuggestionsForPlan({
    runPlan: isSignsAndSymptoms,
    includeDiseaseMetrics: false,
    categories: isSignsAndSymptoms
      ? isSymptoms
        ? CATEGORIES_FOR_MOST_RELEVANT_ROS
        : CATEGORIES_FOR_MOST_RELEVANT_PE
      : undefined,
  });

  const ddLoading = (ddLoadingState || planLoading) && isSignsAndSymptoms;

  const { panels } = useMemo(
    () =>
      selection.subgroup
        ? panelsInSubGroup(selection.subgroup)
        : { panels: [] },
    [selection.subgroup, panelsInSubGroup],
  );

  const suggestionsSignsSymptoms = useMemo(
    () =>
      nextStepSuggestions
        .filter((s) => !s.finding.qualifiers.length)
        .slice(0, MAX_MOST_LIKELY_SUGGESTIONS),
    [nextStepSuggestions],
  );

  function suggestionsAsPanel(title: string, suggestions: QualifiedFeature[]) {
    const panel: DataInputPanel = {
      title: title,
      id: title,
      findings: suggestions || [],
    };
    return panel;
  }

  const suggestionsNextStep = useMemo(
    () =>
      suggestionsAsPanel(
        MOST_LIKELY_ID,
        suggestionsSignsSymptoms.map((s) => s.finding),
      ),
    [suggestionsSignsSymptoms],
  );

  const panelsSortedByName = useMemo(
    () => (panels ? panels.sort(compareByShortName) : []),
    [panels],
  );

  const customFindings = useMemo(() => {
    return customFindingsInSubGroup(
      selection.subgroup,
      currentCase[selection.section],
      selection.section,
    );
  }, [selection.subgroup, currentCase, selection.section]);

  const listWResponses = useMemo(() => {
    return buildPanelListWithResponsesAndSuggestions(
      panelsSortedByName,
      customFindings,
      getCurrentResponse,
      selection.section,
    );
  }, [
    panelsSortedByName,
    customFindings,
    getCurrentResponse,
    selection.section,
  ]);

  const subCategories = useMemo(() => {
    if (isSignsAndSymptoms) {
      return [suggestionsNextStep, ...listWResponses];
    }
    return listWResponses;
  }, [isSignsAndSymptoms, suggestionsNextStep, listWResponses]);

  const [currentSubCat, setSubCat] = useState(
    selectedPanelId || subCategories[0]?.id,
  );

  useEffect(() => {
    // Change from controlled to uncontrolled and handle reset, if chaged to null
    setSubCat(selectedPanelId || subCategories?.[0]?.id);
  }, [setSubCat, selectedPanelId, subCategories]);

  // the currently selected panel
  const curPanel = useMemo(() => {
    return subCategories?.length === 1
      ? subCategories[0] // just one panel? just show it
      : subCategories.find((l) => l.id === currentSubCat) || subCategories?.[0]; // else, show the first panel
  }, [subCategories, currentSubCat]);

  // metadata about this section
  const config = useMemo(
    () => (selection.subgroup ? configForCategory(selection.subgroup) : null),
    [selection.subgroup],
  );

  const createListClickHandler = useCallback(
    (listPanelItem) => () => {
      const selectedSubCat = subCategories.find(
        (tab) => tab.id === listPanelItem.id,
      );
      const name = selectedSubCat?.name;
      evt({
        category: inWizard
          ? CATEGORY.AGENT_WIZARD
          : CATEGORY.AGENT_PATIENT_VIEW,
        action: ACTION.SELECT_TAB_SUBGROUP,
        label: name,
      });

      if (selection.subgroup.id === KahunPanelSubGroup.Labs) {
        navigateTo({
          grouping: selection.section,
          subGroupId: selection.subgroup.id,
          panelId: listPanelItem.id,
        });
      } else {
        setIsUserSelectedPanel(true);
        setSubCat(selectedSubCat.id);
      }
    },
    [
      subCategories,
      selection.section,
      inWizard,
      selection.subgroup,
      navigateTo,
      setSubCat,
    ],
  );

  const handlePanelsSubCatMenuSelect = useCallback(
    (sg) => {
      navigateTo({
        grouping: selection.section,
        subGroupId: sg.id,
      });
    },
    [selection.section, navigateTo],
  );

  const onChangeSection = (section: PatientCaseGrouping) => {
    props.onChangeSection(section);
  };

  const loading = props.loading || ddLoading;

  /***
   * What panel is open right now? used to get the right configuration regarding
   * search, refinement capabilities, and relations which should be assigned on
   * the selected item (such as family history, or habit)
   */
  const navigationContext = useMemo(
    () =>
      ({
        caseGrouping: config?.overrideSection || selection.section,
        panelCategory: selection.subgroup,
        panel: curPanel,
      } as PanelContext),
    [config, selection.section, selection.subgroup, curPanel],
  );

  const mostRelevantSelected = selectedPanelID === MOST_LIKELY_ID;

  useForceDDOnceInWizard(!isUserSelectedPanel || mostRelevantSelected);

  const goToPatient = useCallback(() => {
    navigateTo({ route: AgentRoute.PatientChart });
  }, [navigateTo]);

  const { isMobile } = useScreenSize();
  return (
    <PanelsListView>
      {props.showTabs && (
        <SectionTabs
          changeSection={onChangeSection}
          section={selection.section}
          showRoundedBottom={false}
        />
      )}

      {!props.showTabs && (
        <PanelTop>
          <PanelTitle
            selection={selection}
            title={sectionTitle}
            onBackClicked={!inWizard ? goToPatient : null}
          />
          {/*Desktop*/}
          {selection.subgroup &&
            selection.subgroup.id !== KahunPanelSubGroup.VitalSign && (
              <PanelsSearch searchContext={navigationContext} />
            )}
        </PanelTop>
      )}

      {!singlePanel && (
        <PanelSubTop
          className={"panel-sub-top"}
          tabs={props.showTabs ? "true" : "false"}
        >
          <SubCategoryArea className={"sub-category-area"}>
            <PanelsSubGroupMenu
              subGroups={subGroups}
              selection={props.selection?.subgroup?.id || ""}
              onSelect={handlePanelsSubCatMenuSelect}
              invertColors={props.showTabs || !isMobile}
            />
          </SubCategoryArea>
        </PanelSubTop>
      )}

      <PanelContent>
        <CategorySection>
          <PanelsSubCategorySelect
            subCategories={subCategories}
            selectPanel={createListClickHandler}
            selectedPanelId={currentSubCat || subCategories[0]?.id}
          />
          {/*Mobile*/}
          {props.showTabs &&
            selection.subgroup &&
            selection.subgroup.id !== KahunPanelSubGroup.VitalSign && (
              <PanelsSearch searchContext={navigationContext} />
            )}
        </CategorySection>
        {curPanel && (
          <>
            {loading && <ThreeDotsLoader />}
            {!loading && (
              <DataInputPanelEdit
                panelContext={navigationContext}
                getCaseValue={props.getCurrentResponse}
                setCaseValue={props.setResponse}
                drugFeature={drugFeature}
              />
            )}
          </>
        )}
      </PanelContent>
    </PanelsListView>
  );
};
